import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { subscribeToCustodySchedules, subscribeToCustodyOverrides } from '@coparent/shared/src/services/custodyService';
import { subscribeToExpenses, subscribeToRecurringExpenses, calculateBalance } from '@coparent/shared/src/services/expenseService';
import { subscribeToActivities, addExcludedDate } from '@coparent/shared/src/services/activityService';
import { subscribeToChores, subscribeToDailyCompletions, completeChore, uncompleteChore } from '@coparent/shared/src/services/choreService';
import { subscribeToRules } from '@coparent/shared/src/services/ruleService';
import { expandCustodySchedule, applyOverrides, buildFullCustodyMap } from '@coparent/shared/src/utils/custodyUtils';
import { formatCurrency } from '../utils/expenseUtils';
import useActivityExpenseSync from '../hooks/useActivityExpenseSync';
import { format, addDays, startOfWeek, endOfWeek, startOfDay } from 'date-fns';
import { toJsDate } from '@coparent/shared/src/utils/dateUtils';
import { DAYS_OF_WEEK_SHORT } from '@coparent/shared/src/utils/constants';
import { getChildIds } from '@coparent/shared/src/utils/childUtils';
import ChildDots from '../components/common/ChildDots';
import LogisticsCard from '../components/dashboard/LogisticsCard';
import ActivityInstanceModal from '../components/activities/ActivityInstanceModal';
import toast from 'react-hot-toast';
import {
  HiCalendar,
  HiCurrencyDollar,
  HiClipboardList,
  HiArrowRight,
  HiSwitchHorizontal,
  HiCheckCircle,
  HiShieldCheck,
} from 'react-icons/hi';
import NotificationBell from '../components/notifications/NotificationBell';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { family, familyId, childrenMap, hasFamily, getParticipantProfile, getAllParticipants } = useFamily();
  const [schedules, setSchedules] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [activities, setActivities] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [chores, setChores] = useState([]);
  const [choreCompletions, setChoreCompletions] = useState([]);
  const [rules, setRules] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    if (!familyId) return;
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const unsubs = [
      subscribeToCustodySchedules(familyId, setSchedules),
      subscribeToCustodyOverrides(familyId, setOverrides),
      subscribeToExpenses(familyId, setExpenses),
      subscribeToActivities(familyId, setActivities),
      subscribeToRecurringExpenses(familyId, setRecurringExpenses),
      subscribeToChores(familyId, setChores),
      subscribeToDailyCompletions(familyId, todayStr, setChoreCompletions),
      subscribeToRules(familyId, setRules),
    ];
    return () => unsubs.forEach(u => u());
  }, [familyId]);

  const today = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => startOfWeek(today), [today]);
  const weekEnd = useMemo(() => endOfWeek(today), [today]);
  const logisticsStart = useMemo(() => startOfDay(today), [today]);
  const logisticsEnd = useMemo(() => addDays(logisticsStart, 6), [logisticsStart]);

  const logisticsCustodyMap = useMemo(() => {
    if (!family) return {};
    return buildFullCustodyMap(schedules, overrides, family, logisticsStart, logisticsEnd);
  }, [schedules, overrides, family, logisticsStart, logisticsEnd]);

  const weekCustody = useMemo(() => {
    if (!family) return {};

    let result = {};

    if (schedules.length) {
      const schedule = schedules[0];
      const expanded = expandCustodySchedule(schedule, weekStart, weekEnd);
      result = applyOverrides(expanded, overrides, family.parentIds);
    }

    return result;
  }, [schedules, overrides, family, weekStart, weekEnd]);

  // Helper to get parent label (respects custom labels like CalendarView)
  const getParentLabel = (pid) => {
    if (!family) return '';
    const label = family.settings?.parentLabels?.[pid];
    if (label) return label;
    return family.parentProfiles[pid]?.displayName?.split(' ')[0] || '';
  };

  const pendingExpenses = useMemo(() => expenses.filter(e => e.status === 'pending'), [expenses]);
  const balance = useMemo(() => {
    if (family?.parentIds?.length !== 2) return { amount: 0 };
    return calculateBalance(expenses, family.parentIds, family.householdMembers || {});
  }, [expenses, family]);

  // Auto-create expenses for past activity occurrences with costs
  useActivityExpenseSync(familyId, activities, expenses, currentUser, recurringExpenses);

  const pendingOverrides = overrides.filter(
    o => o.status === 'proposed' && o.proposedBy !== currentUser.uid
  );

  const pendingRulesList = useMemo(
    () => rules.filter(r => r.status === 'proposed' && r.proposedBy !== currentUser.uid),
    [rules, currentUser.uid]
  );

  const todaysChores = useMemo(() => {
    const dow = new Date().getDay();
    return chores.filter(c => {
      if (c.frequency === 'daily') return true;
      return c.dayOfWeek?.includes(dow);
    });
  }, [chores]);

  const choresDoneCount = useMemo(() => {
    const totalSlots = todaysChores.reduce((sum, c) => sum + Math.max(c.childIds?.length || 0, 1), 0);
    const doneSlots = choreCompletions.length;
    return { done: doneSlots, total: totalSlots };
  }, [todaysChores, choreCompletions]);

  const handleLogisticsEntryClick = (entry) => {
    const activity = activities.find(a => a.id === entry.activityId);
    if (!activity) return;

    const instanceOverride = activity.instanceOverrides?.[entry.date] || {};
    const startTime = instanceOverride.startTime ?? activity.startTime;
    const endTime = instanceOverride.endTime ?? activity.endTime;
    const location = instanceOverride.location ?? activity.location;
    const dropoffParentId = instanceOverride.dropoffParentId ?? activity.dropoffParentId;
    const pickupParentId = instanceOverride.pickupParentId ?? activity.pickupParentId;

    const d = new Date(entry.date + 'T00:00:00');
    let eventStart = new Date(d);
    if (startTime) {
      const [h, m] = startTime.split(':').map(Number);
      eventStart.setHours(h, m, 0, 0);
    }

    setSelectedEvent({
      start: eventStart,
      resource: {
        type: 'activity',
        isRecurring: activity.type !== 'event',
        activityId: activity.id,
        activityName: activity.name,
        childIds: getChildIds(activity),
        startTime,
        endTime,
        location,
        notes: activity.notes || null,
        cost: activity.cost || null,
        date: entry.date,
        dropoffParentId,
        pickupParentId,
        usesCustodyDefault: activity.usesCustodyDefault,
        hasCost: !!activity.cost,
        hasInstanceOverride: !!Object.keys(instanceOverride).length,
        color: activity.color || '#22c55e',
      },
    });
  };

  const handleSkipInstance = async () => {
    if (!selectedEvent?.resource) return;
    const { activityId, date } = selectedEvent.resource;
    try {
      await addExcludedDate(familyId, activityId, date, currentUser?.uid || null);
      toast.success('Instance removed');
      setSelectedEvent(null);
    } catch (error) {
      toast.error('Failed to remove instance');
    }
  };

  const handleToggleChore = async (choreId, childId) => {
    const existing = choreCompletions.find(c => c.choreId === choreId && c.childId === childId);
    if (existing) {
      await uncompleteChore(familyId, choreId, existing.id);
    } else {
      await completeChore(familyId, choreId, {
        date: format(new Date(), 'yyyy-MM-dd'),
        childId,
        completedBy: currentUser.uid,
        household: currentUser.uid,
      });
    }
  };

  if (!hasFamily) {
    return (
      <div className="max-w-lg mx-auto text-center py-20 animate-fade-in">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-ink-800 mb-6 shadow-warm-lg">
          <span className="text-white text-xl font-bold">Co</span>
        </div>
        <h1 className="text-3xl font-bold text-ink-900 tracking-tight mb-3">Welcome to CoParent</h1>
        <p className="text-ink-400 mb-8 max-w-sm mx-auto">Get started by creating a family or joining one with an invite code.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/onboarding" className="btn-primary px-8">
            Create a Family
          </Link>
          <Link to="/join" className="btn-secondary px-8">
            Join with Invite Code
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between page-header">
        <div>
          <h1 className="page-title text-[1.75rem]">
            Hi {currentUser?.displayName?.split(' ')[0]}
          </h1>
          <p className="page-subtitle">Here's your week at a glance</p>
        </div>
        <NotificationBell />
      </div>

      {/* Pending swap requests */}
      {pendingOverrides.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200/60 rounded-2xl p-4 animate-slide-up">
          <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-2">
            <HiSwitchHorizontal className="text-base" />
            Pending Swap Requests
          </div>
          {pendingOverrides.map(o => (
            <div key={o.id} className="text-sm text-amber-800/80">
              {getParticipantProfile(o.proposedBy)?.displayName || 'Someone'} wants to swap{' '}
              {format(toJsDate(o.date), 'MMM d')}
              {o.note && ` — "${o.note}"`}
            </div>
          ))}
          <Link to="/swaps" className="text-sm text-amber-700 font-semibold hover:text-amber-800 mt-2 inline-flex items-center gap-1 transition-colors">
            Review swaps <HiArrowRight className="text-xs" />
          </Link>
        </div>
      )}

      {/* Pending rule proposals */}
      {pendingRulesList.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200/60 rounded-2xl p-4 animate-slide-up">
          <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-2">
            <HiShieldCheck className="text-base" />
            Pending Household Rules
          </div>
          {pendingRulesList.map(r => (
            <div key={r.id} className="text-sm text-amber-800/80">
              {getParticipantProfile(r.proposedBy)?.displayName || 'Co-parent'} proposed: "{r.title}"
            </div>
          ))}
          <Link to="/rules" className="text-sm text-amber-700 font-semibold hover:text-amber-800 mt-2 inline-flex items-center gap-1 transition-colors">
            Review rules <HiArrowRight className="text-xs" />
          </Link>
        </div>
      )}

      {/* This week's custody */}
      <div className="card p-6 mb-4">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem]">
            <div className="w-7 h-7 rounded-lg bg-parent1-50 flex items-center justify-center">
              <HiCalendar className="text-parent1-500 text-sm" />
            </div>
            This Week
          </h2>
          <Link to="/calendar" className="text-xs text-ink-400 hover:text-ink-700 flex items-center gap-1 font-semibold transition-colors">
            Full Calendar <HiArrowRight className="text-[0.625rem]" />
          </Link>
        </div>
        {/* Parent legend */}
        {family?.parentIds?.length > 0 && (
          <div className="flex items-center gap-4 mb-4">
            {family.parentIds.map(pid => {
              const profile = family.parentProfiles?.[pid];
              return (
                <div key={pid} className="flex items-center gap-1.5">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: profile?.color || '#d6d3d1' }}
                  />
                  <span className="text-xs text-ink-500 font-medium">{getParentLabel(pid)}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: 7 }, (_, i) => {
            const day = addDays(weekStart, i);
            const dateStr = format(day, 'yyyy-MM-dd');
            const entry = weekCustody[dateStr];
            const parentIndex = typeof entry === 'number' ? entry : entry?.parentIndex;
            const isSwapped = typeof entry === 'object' && entry?.isOverride;
            const isPending = typeof entry === 'object' && entry?.status === 'proposed';
            const parentId = parentIndex ? family?.parentIds?.[parentIndex - 1] : null;
            const profile = parentId ? family?.parentProfiles?.[parentId] : null;
            const isToday = format(today, 'yyyy-MM-dd') === dateStr;
            const parentName = parentId ? getParentLabel(parentId) : null;

            return (
              <div
                key={i}
                className={`text-center py-3 rounded-xl transition-all duration-200 relative ${
                  isToday ? 'ring-2 ring-ink-800 ring-offset-2' : ''
                } ${isPending ? 'opacity-60' : ''}`}
                style={{
                  backgroundColor: profile?.color ? profile.color + '18' : '#f5f1ec',
                  borderBottom: isSwapped && !isPending ? `2px dashed ${profile?.color || '#78716c'}` : undefined,
                }}
              >
                {isSwapped && (
                  <div className="absolute top-1 right-1">
                    <HiSwitchHorizontal
                      className="text-[0.625rem]"
                      style={{ color: isPending ? '#d97706' : (profile?.color || '#78716c') }}
                    />
                  </div>
                )}
                <p className="text-[0.625rem] text-ink-400 font-bold uppercase tracking-wider">{DAYS_OF_WEEK_SHORT[i]}</p>
                <p className="text-lg font-bold text-ink-800 mt-0.5">{format(day, 'd')}</p>
                {parentName ? (
                  <p
                    className="text-[0.5625rem] font-bold mt-1 truncate px-1 leading-tight"
                    style={{ color: profile?.color || '#78716c' }}
                  >
                    {parentName}
                  </p>
                ) : (
                  <div
                    className="w-1.5 h-1.5 rounded-full mx-auto mt-1.5"
                    style={{ backgroundColor: '#d6d3d1' }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Your Logistics — next 7 days */}
      {family?.parentIds?.length > 0 && (
        <LogisticsCard
          activities={activities}
          custodyMap={logisticsCustodyMap}
          parentIds={family.parentIds}
          userId={currentUser?.uid}
          rangeStart={logisticsStart}
          rangeEnd={logisticsEnd}
          childrenMap={childrenMap}
          today={today}
          onEntryClick={handleLogisticsEntryClick}
        />
      )}

      {selectedEvent && (
        <ActivityInstanceModal
          event={selectedEvent}
          family={family}
          familyId={familyId}
          childrenMap={childrenMap}
          allParticipants={getAllParticipants()}
          onClose={() => setSelectedEvent(null)}
          onSkip={handleSkipInstance}
          readOnly
        />
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* Expense balance */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem]">
              <div className="w-7 h-7 rounded-lg bg-shared-50 flex items-center justify-center">
                <HiCurrencyDollar className="text-shared-500 text-sm" />
              </div>
              Expenses
            </h2>
            <Link to="/expenses" className="text-xs text-ink-400 hover:text-ink-700 flex items-center gap-1 font-semibold transition-colors">
              View All <HiArrowRight className="text-[0.625rem]" />
            </Link>
          </div>
          {balance.amount > 0 ? (
            <div className="text-center py-3">
              <p className="text-3xl font-bold text-ink-900 tracking-tight">{formatCurrency(balance.amount)}</p>
              <p className="text-sm text-ink-400 mt-1.5">
                {balance.owerId === currentUser.uid
                  ? `You owe ${getParticipantProfile(balance.oweeId)?.displayName || 'co-parent'}`
                  : `${getParticipantProfile(balance.owerId)?.displayName || 'Co-parent'} owes you`
                }
              </p>
            </div>
          ) : (
            <p className="text-center py-5 text-ink-300 text-sm font-medium">All settled up</p>
          )}
          <p className="text-xs text-ink-300 text-center mt-1">{pendingExpenses.length} pending expenses</p>
        </div>

        {/* Activities */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem]">
              <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center">
                <HiClipboardList className="text-purple-500 text-sm" />
              </div>
              Activities
            </h2>
            <Link to="/activities" className="text-xs text-ink-400 hover:text-ink-700 flex items-center gap-1 font-semibold transition-colors">
              View All <HiArrowRight className="text-[0.625rem]" />
            </Link>
          </div>
          {activities.length > 0 ? (
            <div className="space-y-2">
              {activities.slice(0, 4).map(a => (
                <div key={a.id} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-ink-700 font-medium flex items-center gap-1.5">
                    {a.name}
                    <ChildDots childIds={getChildIds(a)} childrenMap={childrenMap} size="xs" />
                  </span>
                  <div className="flex gap-1">
                    {a.type === 'event' && a.eventDate ? (
                      <span className="text-[0.625rem] px-1.5 py-0.5 bg-surface-100 text-ink-500 rounded font-bold uppercase">
                        {new Date(a.eventDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    ) : (
                      a.dayOfWeek?.map(d => (
                        <span key={d} className="text-[0.625rem] px-1.5 py-0.5 bg-surface-100 text-ink-500 rounded font-bold uppercase">
                          {DAYS_OF_WEEK_SHORT[d]}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-5 text-ink-300 text-sm font-medium">No activities yet</p>
          )}
        </div>
      </div>

      {/* Today's Chores */}
      <div className="card p-6 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem]">
              <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                <HiCheckCircle className="text-emerald-500 text-sm" />
              </div>
              Chores
            </h2>
            <Link to="/chores" className="text-xs text-ink-400 hover:text-ink-700 flex items-center gap-1 font-semibold transition-colors">
              View All <HiArrowRight className="text-[0.625rem]" />
            </Link>
          </div>
          {todaysChores.length > 0 ? (
            <>
              <p className="text-xs text-ink-300 mb-3">{choresDoneCount.done}/{choresDoneCount.total} done today</p>
              <div className="space-y-2">
                {todaysChores.slice(0, 4).map(chore => (
                  <div key={chore.id} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-ink-700 font-medium flex items-center gap-1.5">
                      {chore.name}
                      {chore.childIds?.length > 0 && (
                        <ChildDots childIds={chore.childIds} childrenMap={childrenMap} size="xs" />
                      )}
                    </span>
                    <div className="flex gap-1.5">
                      {chore.childIds?.length > 0 ? chore.childIds.map(childId => {
                        const done = choreCompletions.some(c => c.choreId === chore.id && c.childId === childId);
                        return (
                          <button
                            key={childId}
                            onClick={() => handleToggleChore(chore.id, childId)}
                            className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                              done ? 'bg-green-500 text-white' : 'bg-surface-100 text-ink-300 hover:bg-surface-200'
                            }`}
                          >
                            <HiCheckCircle className="text-xs" />
                          </button>
                        );
                      }) : (
                        <button
                          onClick={() => handleToggleChore(chore.id, 'all')}
                          className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                            choreCompletions.some(c => c.choreId === chore.id)
                              ? 'bg-green-500 text-white'
                              : 'bg-surface-100 text-ink-300 hover:bg-surface-200'
                          }`}
                        >
                          <HiCheckCircle className="text-xs" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-center py-5 text-ink-300 text-sm font-medium">No chores today</p>
          )}
        </div>
    </div>
  );
};

export default Dashboard;
